import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  DOCUMENT,
  ChangeDetectionStrategy,
} from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { WINDOW } from "../../../shared/helpers/window.helper";
import { catchError, filter, of, Observable, skip, Subscription, take } from "rxjs";
import { AuthenticationService } from "../../../shared/services/authentication.service";
import { NavigationEnd, Router } from "@angular/router";
import { LocalStorageService } from "../../../shared/services/local-storage.service";
import { SubscriptionService } from "../../../shared/services/subscription.service";
import { AnalyticsService } from "../../../shared/services/analytics.service";
import { GroupService } from "../../../shared/services/group.service";

@Component({
  selector: "app-header",
  templateUrl: "./header.component.html",
  styleUrls: ["./header.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class HeaderComponent implements OnInit, OnDestroy {
  isFixed?: boolean;
  loggedIn: boolean | null = null;
  loggedInEmail = "";
  menuOpened = false;
  // Starts false on both server and browser so the prerendered shell never
  // shows the link, and it only appears once the /group/mine fetch below
  // resolves in the browser (no hydration mismatch).
  hasGroups = false;

  private loggedInSub?: Subscription;
  private loggedInEmailSub?: Subscription;
  private routerSub?: Subscription;
  private lastUrl = "";

  //@HostBinding('class.menu-opened') menuOpened = false;
  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(WINDOW) private window: Window,
    @Inject(PLATFORM_ID) private platformId: Object,
    private authService: AuthenticationService,
    private router: Router,
    private storageService: LocalStorageService,
    private subscriptionService: SubscriptionService,
    private cdr: ChangeDetectorRef,
    private analyticsService: AnalyticsService,
    private groupService: GroupService
  ) {}

  ngOnInit() {
    // Browser-only, deferred past the current task: loggedIn starts null so
    // SSR prerenders the logged-out branch. Setting it from localStorage
    // synchronously here would flip it inside the same change-detection
    // pass Angular uses to validate hydration, so both *ngIf="loggedIn" and
    // *ngIf="!loggedIn" branches can end up in the reconciled DOM (mirrors
    // profile.component.ts's subscriptionLoaded deferral for the same
    // hydration-pass hazard). On the server, loggedIn stays null and the
    // prerendered logged-out markup is left untouched.
    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.updateLoginState();
        this.cdr.detectChanges();
        this.refreshGroups();
      }, 0);

      this.loggedInSub = this.authService.isLoggedIn
        .pipe(
          skip(1) // Skip initial emission, we already set from localStorage
        )
        .subscribe((loggedIn) => {
          this.loggedIn = loggedIn;
          if (loggedIn) {
            this.refreshGroups();
          } else {
            this.hasGroups = false;
          }
          this.cdr.detectChanges();
        });

      this.loggedInEmailSub = this.authService.loggedInEmail
        .pipe(skip(1))
        .subscribe((loggedInEmail) => {
          this.loggedInEmail = loggedInEmail;
          this.cdr.detectChanges();
        });

      // Re-check group membership after navigating into or out of /groups
      // (e.g. just created or joined a group) so the link reflects reality
      // without a full reload.
      this.lastUrl = this.router.url;
      this.routerSub = this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          const previousUrl = this.lastUrl;
          this.lastUrl = event.urlAfterRedirects;
          if (previousUrl.startsWith("/groups") || event.urlAfterRedirects.startsWith("/groups")) {
            this.refreshGroups();
          }
        });
    }
  }

  /**
   * `/group/mine` gates the "Your groups" nav link (backlog #316): groups
   * are B2B-only, so most users have none and shouldn't see it. Only runs
   * in the browser for a currently logged-in user; errors are swallowed
   * (treated as "no groups") rather than surfaced, since a 403/CORS-blocked
   * response (e.g. deploy previews can't reach the API) must not hide the
   * rest of the header, and AuthInterceptor already handles real 401s.
   */
  private refreshGroups(): void {
    if (!isPlatformBrowser(this.platformId) || this.loggedIn !== true) {
      return;
    }

    this.groupService
      .getMine()
      .pipe(
        catchError(() => of([])),
        take(1)
      )
      .subscribe((groups) => {
        this.hasGroups = groups.length > 0;
        this.cdr.detectChanges();
      });
  }

  private updateLoginState(): void {
    const token = this.storageService.getItem("token");
    this.loggedIn = !!token;

    const email = this.storageService.getItem("email");
    this.loggedInEmail = email || "";
  }

  @HostListener("window:scroll", [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      const offset =
        this.window.pageYOffset ||
        this.document.documentElement.scrollTop ||
        this.document.body.scrollTop ||
        0;
      this.isFixed = offset > 10;
    }
  }

  ngOnDestroy() {
    this.loggedInSub?.unsubscribe();
    this.loggedInEmailSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  trackSignupCtaClick(): void {
    this.analyticsService.trackEvent("Signup CTA Click", {
      location: "navbar",
    });
  }

  logout() {
    this.subscriptionService.clear(); // Clear subscription status on logout
    this.authService.logout();
    this.router.navigate(["/"]).then(() => {
      window.location.reload();
    });
  }
}
